// ===========================================
// Admin Settings API Functions
// Phase 2 Priority 3 - Admin Settings Enhancements
// ===========================================

import { apiClient } from '../services/api';
import type { AvatarSettings } from '../types/avatar.types';
import type {
  InvoiceSettingsResponse,
  InvoiceSettingsInput,
  ReminderStep,
  ReminderStepsResponse,
  ReminderStepCreateInput,
  ReminderStepUpdateInput,
  ColorPaletteSettings,
  ColorPaletteInput,
  AddColorInput,
  AcademicYearSettings,
  AcademicYearInput,
  AdminNavigationSettings,
  AdminNavigationInput,
  CalendarStatusColors,
  CalendarStatusColorsInput,
  RoleColorsSettings,
  RoleColorsInput,
  SchoolInfoSettings,
  SchoolInfoInput,
  AllAdminSettings,
  AdminSettingsResponse,
  PaymentSettings,
  PaymentSettingsInput,
  StripeProcessingRates,
  LegalSettings,
  LegalSettingsInput,
  ExtraLessonSettings,
  ExtraLessonSettingsInput,
  ReportsSettings,
  ReportsSettingsInput,
  ReportSchedule,
  ReportScheduleRecipient,
  ReportScheduleInput,
  ReportScheduleUpdateInput,
} from '../types/adminSettings.types';

// ===========================================
// ALL SETTINGS
// ===========================================

export async function getAllSettings(): Promise<AllAdminSettings> {
  const response = await apiClient.get<AdminSettingsResponse<AllAdminSettings>>(
    '/admin/settings'
  );
  return response.data;
}

export async function resetAllSettings(): Promise<AllAdminSettings> {
  const response = await apiClient.post<AdminSettingsResponse<AllAdminSettings>>(
    '/admin/settings/reset-all'
  );
  return response.data;
}

// ===========================================
// INVOICE SETTINGS
// ===========================================

export async function getInvoiceSettings(): Promise<InvoiceSettingsResponse> {
  const response = await apiClient.get<AdminSettingsResponse<InvoiceSettingsResponse>>(
    '/admin/settings/invoice'
  );
  return response.data;
}

export async function updateInvoiceSettings(
  data: InvoiceSettingsInput
): Promise<InvoiceSettingsResponse> {
  const response = await apiClient.patch<AdminSettingsResponse<InvoiceSettingsResponse>>(
    '/admin/settings/invoice',
    data
  );
  return response.data;
}

export async function resetInvoiceSettings(): Promise<InvoiceSettingsResponse> {
  const response = await apiClient.post<AdminSettingsResponse<InvoiceSettingsResponse>>(
    '/admin/settings/invoice/reset'
  );
  return response.data;
}

// ===========================================
// INVOICE REMINDER STEPS (#372 slice B)
// ===========================================
// Schedule fields only. The step's WORDING is edited through System Emails (`step:<id>`).

export async function getReminderSteps(): Promise<ReminderStepsResponse> {
  const response = await apiClient.get<AdminSettingsResponse<ReminderStepsResponse>>(
    '/admin/settings/invoice/reminder-steps'
  );
  return response.data;
}

export async function createReminderStep(input: ReminderStepCreateInput): Promise<ReminderStep> {
  const response = await apiClient.post<AdminSettingsResponse<ReminderStep>>(
    '/admin/settings/invoice/reminder-steps',
    input
  );
  return response.data;
}

export async function updateReminderStep(stepId: string, input: ReminderStepUpdateInput): Promise<ReminderStep> {
  const response = await apiClient.patch<AdminSettingsResponse<ReminderStep>>(
    `/admin/settings/invoice/reminder-steps/${stepId}`,
    input
  );
  return response.data;
}

export async function deleteReminderStep(stepId: string): Promise<void> {
  await apiClient.delete(`/admin/settings/invoice/reminder-steps/${stepId}`);
}

// ===========================================
// COLOR PALETTE
// ===========================================

export async function getColorPalette(): Promise<ColorPaletteSettings> {
  const response = await apiClient.get<AdminSettingsResponse<ColorPaletteSettings>>(
    '/admin/settings/colors'
  );
  return response.data;
}

export async function updateColorPalette(
  data: ColorPaletteInput
): Promise<ColorPaletteSettings> {
  const response = await apiClient.put<AdminSettingsResponse<ColorPaletteSettings>>(
    '/admin/settings/colors',
    data
  );
  return response.data;
}

export async function addCustomColor(
  color: AddColorInput
): Promise<ColorPaletteSettings> {
  const response = await apiClient.post<AdminSettingsResponse<ColorPaletteSettings>>(
    '/admin/settings/colors',
    color
  );
  return response.data;
}

export async function removeColor(colorValue: string): Promise<ColorPaletteSettings> {
  const encodedValue = encodeURIComponent(colorValue);
  const response = await apiClient.delete<AdminSettingsResponse<ColorPaletteSettings>>(
    `/admin/settings/colors/${encodedValue}`
  );
  return response.data;
}

export async function resetColorPalette(): Promise<ColorPaletteSettings> {
  const response = await apiClient.post<AdminSettingsResponse<ColorPaletteSettings>>(
    '/admin/settings/colors/reset'
  );
  return response.data;
}

// ===========================================
// ACADEMIC YEAR
// ===========================================

export async function getAcademicYear(): Promise<AcademicYearSettings> {
  const response = await apiClient.get<AdminSettingsResponse<AcademicYearSettings>>(
    '/admin/settings/academic-year'
  );
  return response.data;
}

export async function updateAcademicYear(
  data: AcademicYearInput
): Promise<AcademicYearSettings> {
  const response = await apiClient.put<AdminSettingsResponse<AcademicYearSettings>>(
    '/admin/settings/academic-year',
    data
  );
  return response.data;
}

// ===========================================
// ADMIN NAVIGATION
// ===========================================

export async function getAdminNavigation(): Promise<AdminNavigationSettings> {
  const response = await apiClient.get<AdminSettingsResponse<AdminNavigationSettings>>(
    '/admin/settings/navigation'
  );
  return response.data;
}

export async function updateAdminNavigation(
  data: AdminNavigationInput
): Promise<AdminNavigationSettings> {
  const response = await apiClient.put<AdminSettingsResponse<AdminNavigationSettings>>(
    '/admin/settings/navigation',
    data
  );
  return response.data;
}

export async function resetAdminNavigation(): Promise<AdminNavigationSettings> {
  const response = await apiClient.post<AdminSettingsResponse<AdminNavigationSettings>>(
    '/admin/settings/navigation/reset'
  );
  return response.data;
}

// ===========================================
// CALENDAR STATUS COLORS
// ===========================================

export async function getCalendarStatusColors(): Promise<CalendarStatusColors> {
  const response = await apiClient.get<AdminSettingsResponse<CalendarStatusColors>>(
    '/admin/settings/calendar-colors'
  );
  return response.data;
}

export async function updateCalendarStatusColors(
  data: CalendarStatusColorsInput
): Promise<CalendarStatusColors> {
  const response = await apiClient.put<AdminSettingsResponse<CalendarStatusColors>>(
    '/admin/settings/calendar-colors',
    data
  );
  return response.data;
}

export async function resetCalendarStatusColors(): Promise<CalendarStatusColors> {
  const response = await apiClient.post<AdminSettingsResponse<CalendarStatusColors>>(
    '/admin/settings/calendar-colors/reset'
  );
  return response.data;
}

// ===========================================
// ROLE COLORS
// ===========================================

export async function getRoleColors(): Promise<RoleColorsSettings> {
  const response = await apiClient.get<AdminSettingsResponse<RoleColorsSettings>>(
    '/admin/settings/role-colors'
  );
  return response.data;
}

export async function updateRoleColors(
  data: RoleColorsInput
): Promise<RoleColorsSettings> {
  const response = await apiClient.put<AdminSettingsResponse<RoleColorsSettings>>(
    '/admin/settings/role-colors',
    data
  );
  return response.data;
}

export async function resetRoleColors(): Promise<RoleColorsSettings> {
  const response = await apiClient.post<AdminSettingsResponse<RoleColorsSettings>>(
    '/admin/settings/role-colors/reset'
  );
  return response.data;
}

// ===========================================
// OWNER TITLE (roles slice 2)
// ===========================================

export interface OwnerTitle {
  ownerTitle: string;
  isDefault: boolean;
  /** Messages labels an Admin with the title instead of "School office". */
  inMessages: boolean;
}

/** A key left out is left as it is. */
export interface OwnerTitleUpdate {
  ownerTitle?: string | null;
  inMessages?: boolean;
}

/** Any signed-in user may read it: every view names the owner with it. */
export async function getOwnerTitle(): Promise<OwnerTitle> {
  const response = await apiClient.get<AdminSettingsResponse<OwnerTitle>>('/admin/settings/owner-title');
  return response.data;
}

/** Owner only. A null or blank title clears it back to the default. */
export async function updateOwnerTitle(update: OwnerTitleUpdate): Promise<OwnerTitle> {
  const response = await apiClient.put<AdminSettingsResponse<OwnerTitle>>('/admin/settings/owner-title', update);
  return response.data;
}

// ===========================================
// SCHOOL INFO
// ===========================================

export async function getSchoolInfo(): Promise<SchoolInfoSettings> {
  const response = await apiClient.get<AdminSettingsResponse<SchoolInfoSettings>>(
    '/admin/settings/school-info'
  );
  return response.data;
}

export async function updateSchoolInfo(
  data: SchoolInfoInput
): Promise<SchoolInfoSettings> {
  const response = await apiClient.put<AdminSettingsResponse<SchoolInfoSettings>>(
    '/admin/settings/school-info',
    data
  );
  return response.data;
}

export async function resetSchoolInfo(): Promise<SchoolInfoSettings> {
  const response = await apiClient.post<AdminSettingsResponse<SchoolInfoSettings>>(
    '/admin/settings/school-info/reset'
  );
  return response.data;
}

export async function uploadSchoolLogo(file: File): Promise<{ logoUrl: string }> {
  const formData = new FormData();
  formData.append('logo', file);
  const response = await apiClient.post<AdminSettingsResponse<{ logoUrl: string }>>(
    '/admin/settings/school-info/logo',
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return response.data;
}

export async function removeSchoolLogo(): Promise<{ logoUrl: null }> {
  const response = await apiClient.delete<AdminSettingsResponse<{ logoUrl: null }>>(
    '/admin/settings/school-info/logo'
  );
  return response.data;
}

// ===========================================
// LEGAL SETTINGS
// ===========================================

export async function getLegalSettings(): Promise<LegalSettings> {
  const response = await apiClient.get<AdminSettingsResponse<LegalSettings>>(
    '/admin/settings/legal'
  );
  return response.data;
}

export async function updateLegalSettings(
  data: LegalSettingsInput
): Promise<LegalSettings> {
  const response = await apiClient.put<AdminSettingsResponse<LegalSettings>>(
    '/admin/settings/legal',
    data
  );
  return response.data;
}

export async function resetLegalSettings(): Promise<LegalSettings> {
  const response = await apiClient.post<AdminSettingsResponse<LegalSettings>>(
    '/admin/settings/legal/reset'
  );
  return response.data;
}

// ===========================================
// AVATAR SETTINGS (Priority 11)
// ===========================================

export async function getAvatarSettings(): Promise<AvatarSettings> {
  const response = await apiClient.get<AdminSettingsResponse<AvatarSettings>>(
    '/admin/settings/avatar'
  );
  return response.data;
}

export async function updateAvatarSettings(
  data: Partial<AvatarSettings>
): Promise<AvatarSettings> {
  const response = await apiClient.put<AdminSettingsResponse<AvatarSettings>>(
    '/admin/settings/avatar',
    data
  );
  return response.data;
}

export async function resetAvatarSettings(): Promise<AvatarSettings> {
  const response = await apiClient.post<AdminSettingsResponse<AvatarSettings>>(
    '/admin/settings/avatar/reset'
  );
  return response.data;
}

// ===========================================
// PAYMENT SETTINGS
// ===========================================

export async function getPaymentRates(): Promise<StripeProcessingRates> {
  const response = await apiClient.get<AdminSettingsResponse<StripeProcessingRates>>(
    '/admin/settings/payment/rates'
  );
  return response.data;
}

export async function getPaymentSettings(): Promise<PaymentSettings> {
  const response = await apiClient.get<AdminSettingsResponse<PaymentSettings>>(
    '/admin/settings/payment'
  );
  return response.data;
}

export async function updatePaymentSettings(
  data: PaymentSettingsInput
): Promise<PaymentSettings> {
  const response = await apiClient.patch<AdminSettingsResponse<PaymentSettings>>(
    '/admin/settings/payment',
    data
  );
  return response.data;
}

export async function resetPaymentSettings(): Promise<PaymentSettings> {
  const response = await apiClient.post<AdminSettingsResponse<PaymentSettings>>(
    '/admin/settings/payment/reset'
  );
  return response.data;
}

// ===========================================
// EXTRA LESSON SETTINGS
// ===========================================

export async function getExtraLessonSettings(): Promise<ExtraLessonSettings> {
  const response = await apiClient.get<AdminSettingsResponse<ExtraLessonSettings>>(
    '/admin/settings/extra-lesson'
  );
  return response.data;
}

export async function updateExtraLessonSettings(
  data: ExtraLessonSettingsInput
): Promise<ExtraLessonSettings> {
  const response = await apiClient.patch<AdminSettingsResponse<ExtraLessonSettings>>(
    '/admin/settings/extra-lesson',
    data
  );
  return response.data;
}

export async function resetExtraLessonSettings(): Promise<ExtraLessonSettings> {
  const response = await apiClient.post<AdminSettingsResponse<ExtraLessonSettings>>(
    '/admin/settings/extra-lesson/reset'
  );
  return response.data;
}

// ===========================================
// REPORT SETTINGS (2026-09-16)
// ===========================================
// ⚠️ These live under /reports/settings/*, NOT /admin/settings/*, even though
// the data is stored in the same `School.settings` JSON. The routes are gated on
// `reports.read` / `reports.write` rather than `system_settings`, so that a
// custom "reports manager" role can hold them without holding school settings.

export async function getReportsSettings(): Promise<ReportsSettings> {
  const response = await apiClient.get<AdminSettingsResponse<ReportsSettings>>(
    '/reports/settings/overview'
  );
  return response.data;
}

/** ⚠️ PUT, not PATCH — this replaces the whole selection. */
export async function updateReportsSettings(
  data: ReportsSettingsInput
): Promise<ReportsSettings> {
  const response = await apiClient.put<AdminSettingsResponse<ReportsSettings>>(
    '/reports/settings/overview',
    data
  );
  return response.data;
}

export async function resetReportsSettings(): Promise<ReportsSettings> {
  const response = await apiClient.post<AdminSettingsResponse<ReportsSettings>>(
    '/reports/settings/overview/reset'
  );
  return response.data;
}

// ===========================================
// REPORT SCHEDULES (Slice 2, 2026-09-16)
// ===========================================

export async function listReportSchedules(): Promise<ReportSchedule[]> {
  const response = await apiClient.get<AdminSettingsResponse<ReportSchedule[]>>(
    '/reports/settings/schedules'
  );
  return response.data;
}

/** The picker list — same filter the SEND path uses, so it cannot offer someone the sweep would skip. */
export async function listDigestRecipients(): Promise<ReportScheduleRecipient[]> {
  const response = await apiClient.get<AdminSettingsResponse<ReportScheduleRecipient[]>>(
    '/reports/settings/schedules/admin-recipients'
  );
  return response.data;
}

export async function createReportSchedule(data: ReportScheduleInput): Promise<ReportSchedule> {
  const response = await apiClient.post<AdminSettingsResponse<ReportSchedule>>(
    '/reports/settings/schedules',
    data
  );
  return response.data;
}

/** ⚠️ PUT — recipient and report sets are replaced, not merged. */
export async function updateReportSchedule(
  id: string,
  data: ReportScheduleUpdateInput
): Promise<ReportSchedule> {
  const response = await apiClient.put<AdminSettingsResponse<ReportSchedule>>(
    `/reports/settings/schedules/${id}`,
    data
  );
  return response.data;
}

export async function deleteReportSchedule(id: string): Promise<void> {
  await apiClient.delete<AdminSettingsResponse<null>>(`/reports/settings/schedules/${id}`);
}

/** Sends to the REQUESTING admin only, and never consumes the period's ledger claim. */
export async function sendReportScheduleTest(id: string): Promise<{ sentTo: string }> {
  const response = await apiClient.post<AdminSettingsResponse<{ sentTo: string }>>(
    `/reports/settings/schedules/${id}/test`
  );
  return response.data;
}

// ===========================================
// ONBOARDING CONFIG
// ===========================================

export interface OnboardingConfig {
  id: string;
  schoolId: string;
  requireMeetAndGreet: boolean;
  requireRegistrationForMeetAndGreet: boolean;
  requireAdminApproval: boolean;
  autoApproveStudents: boolean;
  allowClassSelectionAtRegistration: boolean;
  waitlistBehavior: 'ADD_TO_WAITLIST' | 'SHOW_ALTERNATIVES' | 'NOTIFY_ADMIN';
  includeFreeTrialClass: boolean;
  freeTrialClassCount: number;
  freeTrialScope: 'PER_STUDENT' | 'PER_FAMILY';
  requirePayment: boolean;
  registrationFee: number | null;
  requireTermsAcceptance: boolean;
  termsAndConditionsUrl: string | null;
  privacyPolicyUrl: string | null;
  termsVersionId: string | null;
  requirePhotoConsent: boolean;
  photoConsentDescription: string | null;
  onboardingExpiryDays: number;
  allowMultiStudentRegistration: boolean;
  customFields: CustomRegistrationField[];
  createdAt: string;
  updatedAt: string;
}

export interface CustomRegistrationField {
  id: string;
  label: string;
  type: 'text' | 'textarea' | 'select' | 'checkbox' | 'radio';
  options?: string[];
  required: boolean;
  sortOrder: number;
}

export interface ToggleValidation {
  type: 'error' | 'warning';
  message: string;
  affectedToggles: string[];
}

export interface OnboardingConfigResponse {
  status: string;
  data: OnboardingConfig;
  validations: ToggleValidation[];
  message?: string;
}

export type OnboardingConfigInput = Partial<
  Omit<OnboardingConfig, 'id' | 'schoolId' | 'createdAt' | 'updatedAt' | 'termsVersionId'>
>;

export async function getOnboardingConfig(): Promise<OnboardingConfigResponse> {
  const response = await apiClient.get<OnboardingConfigResponse>(
    '/admin/settings/onboarding'
  );
  return response;
}

export async function updateOnboardingConfig(
  data: OnboardingConfigInput
): Promise<OnboardingConfigResponse> {
  const response = await apiClient.patch<OnboardingConfigResponse>(
    '/admin/settings/onboarding',
    data
  );
  return response;
}

export async function resetOnboardingConfig(): Promise<OnboardingConfigResponse> {
  const response = await apiClient.post<OnboardingConfigResponse>(
    '/admin/settings/onboarding/reset'
  );
  return response;
}

// ===========================================
// FORCE LOGOUT ALL USERS
// ===========================================

export interface ForceLogoutAllResponse {
  status: string;
  data: { forceLogoutBefore: string };
  message: string;
}

export async function forceLogoutAllUsers(): Promise<ForceLogoutAllResponse> {
  const response = await apiClient.post<ForceLogoutAllResponse>(
    '/admin/settings/force-logout-all'
  );
  return response;
}
